import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import { Shield, X } from "lucide-react";
import { OtpInput } from "../ui";

/* ── Converted to Tailwind utilities in Phase 5 (2026-08-24) ──────────────────
 * TwoFactorModal.scss is deleted (TOKEN_CONTRACT §6.1). Its two keyframes went to
 * `theme.extend.keyframes`, and one of them did not survive the trip: `tfa-slide-up`
 * was byte-identical to the `slide-up` already in the config, so it is reused rather
 * than re-declared. `tfa-shake` is now `shake`.
 *
 * The six code boxes are `ui/OtpInput` — this screen and institute registration had
 * a copy each, and they had already drifted apart. Everything the code field does
 * (auto-advance, backspace-back, paste-to-fill, the error shake) is the control's.
 *
 * The overlay was a third hand-rolled `rgba(0, 0, 0, 0.5)` scrim beside AppModal's
 * and FilterPanel's. It is `--overlay-bg`, which those two now share.
 */

/**
 * TwoFactorModal — Shown after login when user has 2FA enabled.
 * Sends an OTP then verifies it to complete login.
 *
 * Props:
 *   tempToken  — short-lived JWT from login response
 *   method     — 'sms' | 'email'
 *   onSuccess  — callback({ user, token }) on successful verification
 *   onCancel   — callback to dismiss
 */
const STATUS_LINE =
  "tw-text-center tw-text-content-subtle tw-text-sm tw-flex tw-items-center " +
  "tw-justify-center tw-gap-2 tw-my-3";

const TwoFactorModal = ({ tempToken, method, onSuccess, onCancel }) => {
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [maskedTarget, setMaskedTarget] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  // Auto-send OTP on mount
  useEffect(() => {
    sendOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const sendOtp = async () => {
    setSending(true);
    setError("");
    try {
      const { data } = await apiClient.post("/auth/2fa/send", { tempToken });
      setSent(true);
      setMaskedTarget(data.maskedTarget || "");
      setResendTimer(30);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP.");
    } finally {
      setSending(false);
    }
  };

  const handleChange = (value) => {
    setOtp(value);
    setError("");
  };

  const verifyOtp = async (otpStr) => {
    setVerifying(true);
    setError("");
    try {
      const { data } = await apiClient.post("/auth/2fa/verify", {
        tempToken,
        otp: otpStr,
      });
      onSuccess(data);
    } catch (err) {
      setError(err.response?.data?.message || "Verification failed.");
      setOtp("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div
      className="tw-fixed tw-inset-0 tw-bg-[var(--overlay-bg)] tw-flex tw-items-center tw-justify-center tw-z-modal tw-backdrop-blur-[4px]"
      onClick={onCancel}
    >
      <div
        className="tw-bg-surface-raised tw-rounded-lg tw-p-8 tw-w-full tw-max-w-[420px] tw-relative tw-shadow-[0_20px_60px_hsl(var(--surface-dark)/0.15)] tw-animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="tw-absolute tw-top-3 tw-right-3 tw-bg-transparent tw-border-0 tw-text-lg tw-text-content-subtle tw-cursor-pointer tw-px-2 tw-py-1 tw-rounded-[6px] hover:tw-bg-surface-hover hover:tw-text-content-muted"
          onClick={onCancel}
        >
          <X size="1em" />
        </button>

        <div className="tw-text-center tw-mb-6">
          <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-14 tw-h-14 tw-rounded-[14px] tw-bg-gradient-to-br tw-from-primary-solid tw-to-chart-1 tw-text-primary-solid-fg tw-text-2xl">
            <Shield size="1em" />
          </div>
          <h3 className="tw-text-xl tw-font-semibold tw-mt-3 tw-mb-2 tw-text-content">
            Two-Factor Authentication
          </h3>
          <p className="tw-text-sm tw-text-content-subtle tw-m-0 tw-leading-[1.5]">
            {sent
              ? `Enter the 6-digit code sent to ${maskedTarget || (method === "sms" ? "your phone" : "your email")}`
              : `Sending verification code to your ${method === "sms" ? "phone" : "email"}...`}
          </p>
        </div>

        {sending && (
          <div className={STATUS_LINE}>
            <Spinner size="sm" animation="border" /> Sending code...
          </div>
        )}

        {sent && (
          <>
            <OtpInput
              value={otp}
              onChange={handleChange}
              onComplete={verifyOtp}
              invalid={Boolean(error)}
              disabled={verifying}
              autoFocus
              label="Two-factor authentication code"
              className="tw-mb-4 tw-justify-center"
            />

            {error && (
              <p className="tw-text-center tw-text-danger tw-text-[13px] tw-my-2">{error}</p>
            )}

            {verifying && (
              <div className={STATUS_LINE}>
                <Spinner size="sm" animation="border" /> Verifying...
              </div>
            )}

            <div className="tw-text-center tw-mt-4">
              {resendTimer > 0 ? (
                <span className="tw-text-[13px] tw-text-content-subtle">
                  Resend code in {resendTimer}s
                </span>
              ) : (
                <button
                  className="tw-bg-transparent tw-border-0 tw-text-primary tw-text-sm tw-font-medium tw-cursor-pointer tw-px-2 tw-py-1 hover:tw-underline"
                  onClick={sendOtp}
                  disabled={sending}
                >
                  Resend Code
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TwoFactorModal;
